import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Store, Gift, AlertCircle, ShoppingCart, CheckCircle2, ShoppingBag, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/Button';
import { Session } from '@supabase/supabase-js';
import { Reward } from '@/types';

interface RewardsStoreModalProps {
    session: Session;
    isOpen: boolean;
    onClose: () => void;
    onOpenHistory: () => void;
}

export const RewardsStoreModal = ({ session, isOpen, onClose, onOpenHistory }: RewardsStoreModalProps) => {
    const [rewards, setRewards] = useState<Reward[]>([]);
    const [userPoints, setUserPoints] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [redeemingId, setRedeemingId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [expandedIds, setExpandedIds] = useState<string[]>([]);

    // Filtros e Paginação
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // Checkout Flow
    const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
    const [address, setAddress] = useState({
        cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
    });
    const [loadingCep, setLoadingCep] = useState(false);

    const initialLoadRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentPage]);

    useEffect(() => {
        if (isOpen) {
            if (!initialLoadRef.current) {
                fetchData();
                initialLoadRef.current = true;
            }
        } else {
            initialLoadRef.current = false;
            setSuccessMessage(null);
            setErrorMessage(null);
        }
    }, [isOpen, session.user.id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch User Points
            const { data: pointsData, error: pointsError } = await supabase
                .from('my_points_balance')
                .select('balance_points')
                .maybeSingle();

            if (pointsError) throw pointsError;
            setUserPoints(pointsData?.balance_points || 0);

            // Fetch Active Rewards
            const { data: rewardsData, error: rewardsError } = await supabase
                .from('rewards')
                .select('*')
                .eq('status', 'active')
                .order('cost_points', { ascending: true })
                .order('title', { ascending: true });

            if (rewardsError) throw rewardsError;
            setRewards(rewardsData || []);

        } catch (err: any) {
            console.error('Error fetching store data:', err);
            setErrorMessage('Não foi possível carregar a lojinha no momento.');
        } finally {
            setLoading(false);
        }
    };

    const normalizeString = (str: string) => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    };

    const filteredRewards = rewards.filter(reward => {
        const matchesSearch = normalizeString(reward.title).includes(normalizeString(searchTerm));
        const matchesType = selectedType === 'all' || reward.reward_type === selectedType;
        return matchesSearch && matchesType;
    });

    const totalPages = Math.ceil(filteredRewards.length / itemsPerPage);
    const paginatedRewards = filteredRewards.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedType]);

    const handleSelectReward = (reward: Reward) => {
        setErrorMessage(null);
        setSelectedReward(reward);
        if (reward.reward_type === 'product') {
            setAddress({ cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' });
        }
    };

    const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        let rawCep = e.target.value.replace(/\D/g, '');
        if (rawCep.length > 8) rawCep = rawCep.slice(0, 8);
        let formattedCep = rawCep;
        if (rawCep.length > 5) {
            formattedCep = `${rawCep.slice(0, 5)}-${rawCep.slice(5)}`;
        }
        setAddress(prev => ({ ...prev, cep: formattedCep }));

        if (rawCep.length === 8) {
            setLoadingCep(true);
            try {
                const res = await fetch(`https://viacep.com.br/ws/${rawCep}/json/`);
                const data = await res.json();
                if (!data.erro) {
                    setAddress(prev => ({
                        ...prev,
                        street: data.logradouro || '',
                        neighborhood: data.bairro || '',
                        city: data.localidade || '',
                        state: data.uf || ''
                    }));
                }
            } catch (err) {
                console.error('Error fetching cep:', err);
            } finally {
                setLoadingCep(false);
            }
        }
    };

    const handleConfirmRedeem = async () => {
        if (!selectedReward) return;

        if (userPoints < selectedReward.cost_points) {
            setErrorMessage('Pontos insuficientes para este resgate.');
            return;
        }

        if (selectedReward.reward_type === 'product') {
            if (!address.cep || !address.street || !address.number || !address.neighborhood || !address.city || !address.state) {
                setErrorMessage('Por favor, preencha todos os campos obrigatórios do endereço.');
                return;
            }
        }

        setRedeemingId(selectedReward.id);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const metaData = selectedReward.reward_type === 'product' ? {
                shipping_address: address
            } : {};

            const { error: requestError } = await supabase
                .from('points_redemption_requests')
                .insert({
                    reward_id: selectedReward.id,
                    meta: metaData
                });

            if (requestError) throw requestError;

            setSuccessMessage(`A solicitação do "${selectedReward.title}" foi enviada com sucesso! Em breve analisaremos seu pedido.`);
            setSelectedReward(null);

            await fetchData();

        } catch (err: any) {
            console.error('Error redeeming reward:', err);
            setErrorMessage('Ocorreu um erro ao processar seu resgate. Tente novamente mais tarde.');
        } finally {
            setRedeemingId(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-4xl bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-xl text-white shadow-sm">
                            <Store className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-gray-900 dark:text-white leading-tight">Lojinha de Pontos</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Troque seus pontos por benefícios exclusivos</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                            <Gift className="w-4 h-4 text-purple-500" />
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {loading ? '...' : userPoints.toLocaleString('pt-BR')} <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">pts</span>
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800 bg-gray-50/30 dark:bg-black/20"
                >

                    {successMessage ? (
                        <div className="flex flex-col items-center justify-center p-8 text-center h-full min-h-[400px] animate-in zoom-in-95 duration-300">
                            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-3">Resgate Solicitado!</h3>
                            <p className="text-gray-500 dark:text-gray-400 font-medium mb-8 max-w-sm">
                                {successMessage}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                                <Button variant="secondary" onClick={() => setSuccessMessage(null)}>
                                    <Store className="w-4 h-4 mr-2" />
                                    Continuar na Lojinha
                                </Button>
                                <Button
                                    className="bg-purple-600 hover:bg-purple-700 text-white border-0 shadow-lg shadow-purple-500/25"
                                    onClick={onOpenHistory}
                                >
                                    <ShoppingBag className="w-4 h-4 mr-2" />
                                    Meus Resgates
                                </Button>
                            </div>
                        </div>
                    ) : selectedReward ? (
                        <div className="flex flex-col h-full animate-in slide-in-from-right-8 duration-300">
                            <div className="mb-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
                                <div className="w-full md:w-1/3">
                                    <button
                                        onClick={() => setSelectedReward(null)}
                                        className="flex items-center text-sm font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                                        disabled={redeemingId !== null}
                                    >
                                        <ChevronLeft className="w-5 h-5 mr-1" />
                                        Voltar
                                    </button>
                                </div>
                                <div className="w-full md:w-2/3 flex items-center justify-between">
                                    <h3 className="text-xl font-bold dark:text-white">Confirmação de Resgate</h3>
                                </div>
                            </div>

                            {errorMessage && (
                                <div className="mb-6 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-4 rounded-xl border border-red-100 dark:border-red-900/20 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm font-medium">{errorMessage}</p>
                                </div>
                            )}

                            <div className="flex flex-col md:flex-row gap-8 pb-8">
                                {/* Resumo do Pedido */}
                                <div className="w-full md:w-1/3 bg-gray-50 dark:bg-gray-800/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-800 h-fit">
                                    <h4 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                        <ShoppingCart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                        Resumo do Item
                                    </h4>

                                    <div className="flex items-center gap-4 mb-4">
                                        {selectedReward.image_url ? (
                                            <img src={selectedReward.image_url} alt={selectedReward.title} className="w-16 h-16 rounded-xl object-cover bg-gray-100 dark:bg-gray-900" />
                                        ) : (
                                            <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                                                <Gift className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <p className="font-bold text-gray-900 dark:text-white line-clamp-2 text-sm">{selectedReward.title}</p>
                                            <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 mt-1">{selectedReward.cost_points.toLocaleString('pt-BR')} pts</p>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>

                                    <div className="flex justify-between items-center font-bold">
                                        <span className="text-gray-500 dark:text-gray-400 text-sm">Custo total:</span>
                                        <span className="text-lg text-purple-600 dark:text-purple-400">{selectedReward.cost_points.toLocaleString('pt-BR')} pts</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm font-semibold mt-3">
                                        <span className="text-gray-500 dark:text-gray-400">Seu saldo atual:</span>
                                        <span className="text-gray-900 dark:text-white">{userPoints.toLocaleString('pt-BR')} pts</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm font-semibold mt-1">
                                        <span className="text-gray-500 dark:text-gray-400">Saldo após resgate:</span>
                                        <span className="text-gray-900 dark:text-white">{(userPoints - selectedReward.cost_points).toLocaleString('pt-BR')} pts</span>
                                    </div>
                                </div>

                                {/* Formulário ou Info */}
                                <div className="w-full md:w-2/3 flex flex-col items-end">
                                    {selectedReward.reward_type === 'product' ? (
                                        <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm w-full">
                                            <h4 className="font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                                Endereço de Entrega
                                            </h4>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                                                Informe o endereço completo para receber sua recompensa.
                                            </p>

                                            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl flex items-start gap-3">
                                                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                                                <div className="text-sm text-amber-800 dark:text-amber-400 space-y-2">
                                                    <p><strong>🚚 Entrega de Produtos Físicos</strong></p>
                                                    <p>Os produtos físicos estão disponíveis apenas para regiões com cobertura de frete grátis dentro do Brasil.</p>
                                                    <p>Caso o CEP informado não seja elegível para frete grátis, o pedido não poderá ser concluído e será automaticamente cancelado.</p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-4">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="space-y-1 md:col-span-1">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">CEP *</label>
                                                        <div className="relative">
                                                            <input
                                                                type="text"
                                                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                                placeholder="00000-000"
                                                                value={address.cep}
                                                                onChange={handleCepChange}
                                                                maxLength={9}
                                                                disabled={redeemingId !== null}
                                                            />
                                                            {loadingCep && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />}
                                                        </div>
                                                    </div>
                                                    <div className="space-y-1 md:col-span-2">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Logradouro (Rua, Av) *</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            placeholder="Ex: Rua das Flores"
                                                            value={address.street}
                                                            onChange={e => setAddress(p => ({ ...p, street: e.target.value }))}
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Número *</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            placeholder="Ex: 123"
                                                            value={address.number}
                                                            onChange={e => setAddress(p => ({ ...p, number: e.target.value }))}
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                    <div className="space-y-1 md:col-span-2">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Complemento</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            placeholder="Ex: Apto 101, Bloco B"
                                                            value={address.complement}
                                                            onChange={e => setAddress(p => ({ ...p, complement: e.target.value }))}
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Bairro *</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            value={address.neighborhood}
                                                            onChange={e => setAddress(p => ({ ...p, neighborhood: e.target.value }))}
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Cidade *</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            value={address.city}
                                                            onChange={e => setAddress(p => ({ ...p, city: e.target.value }))}
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-bold text-gray-500 dark:text-gray-400 ml-1">Estado (UF) *</label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm dark:text-white"
                                                            value={address.state}
                                                            onChange={e => setAddress(p => ({ ...p, state: e.target.value }))}
                                                            maxLength={2}
                                                            placeholder="UF"
                                                            disabled={redeemingId !== null}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-purple-50 dark:bg-purple-900/10 p-6 rounded-3xl border border-purple-100 dark:border-purple-800 h-full flex flex-col justify-center w-full">
                                            <div className="flex items-center gap-3 mb-3">
                                                <AlertCircle className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                                                <h4 className="font-bold text-gray-900 dark:text-white text-lg">Confirme seu Resgate</h4>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 font-medium leading-relaxed">
                                                Após confirmada, a solicitação passará por análise. Nenhuma quantia em dinheiro será cobrada, apenas os seus pontos.
                                            </p>
                                            <ul className="text-sm font-medium text-gray-500 dark:text-gray-400 space-y-2">
                                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Confira seu saldo final na lateral.</li>
                                                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Acompanhe na aba "Meus Resgates".</li>
                                            </ul>
                                        </div>
                                    )}

                                    <div className="mt-8 flex justify-end gap-3 w-full">
                                        <Button variant="secondary" onClick={() => setSelectedReward(null)} disabled={redeemingId !== null} className="w-full sm:w-auto">
                                            Cancelar
                                        </Button>
                                        <Button
                                            onClick={handleConfirmRedeem}
                                            isLoading={redeemingId !== null}
                                            disabled={redeemingId !== null}
                                            className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:min-w-[200px]"
                                        >
                                            Confirmar Resgate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {errorMessage && (
                                <div className="mb-6 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-4 rounded-xl border border-red-100 dark:border-red-900/20 flex items-start gap-3 animate-in slide-in-from-top-2">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm font-medium">{errorMessage}</p>
                                </div>
                            )}

                            {/* Mobile Balance Badge */}
                            <div className="sm:hidden mb-6 flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-100 dark:border-purple-800/30">
                                <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                <span className="text-base font-bold text-purple-900 dark:text-purple-100">
                                    Seu Saldo: {loading ? '...' : userPoints.toLocaleString('pt-BR')} pts
                                </span>
                            </div>

                            {/* Filters & Search */}
                            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar recompensa..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm dark:text-white placeholder-gray-400"
                                    />
                                </div>
                                <div className="relative sm:w-48">
                                    <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm font-medium dark:text-white"
                                    >
                                        <option value="all">Todos os tipos</option>
                                        <option value="coupon">Cupons</option>
                                        <option value="voucher">Vales / Gift Cards</option>
                                        <option value="product">Produtos</option>
                                    </select>
                                </div>
                            </div>

                            {/* Loading State */}
                            {loading ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 h-80 animate-pulse">
                                            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-t-2xl"></div>
                                            <div className="p-5 space-y-3">
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-xl mt-4"></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredRewards.length === 0 ? (
                                /* Empty State */
                                <div className="flex flex-col items-center justify-center p-12 text-center h-64 bg-white dark:bg-gray-800/50 rounded-3xl border border-gray-100 dark:border-gray-800 border-dashed">
                                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                        <Store className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                        {rewards.length === 0 ? 'Lojinha Vazia' : 'Nenhuma recompensa encontrada'}
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                                        {rewards.length === 0
                                            ? 'Nenhuma recompensa ativa no momento. Volte mais tarde!'
                                            : 'Tente alterar os filtros ou o termo de busca.'}
                                    </p>
                                </div>
                            ) : (
                                /* Rewards Grid */
                                <div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {paginatedRewards.map((reward) => {
                                            const hasEnoughPoints = userPoints >= reward.cost_points;
                                            const isOutOfStock = reward.stock !== null && reward.stock <= 0;
                                            const isRedeeming = redeemingId === reward.id;

                                            return (
                                                <div key={reward.id} className="group flex flex-col bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500/50 shadow-sm hover:shadow-xl hover:shadow-purple-500/10 transition-all overflow-hidden duration-300">

                                                    {/* Image Area */}
                                                    <div className="relative h-48 bg-gray-100 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 overflow-hidden">
                                                        {reward.image_url ? (
                                                            <img
                                                                src={reward.image_url}
                                                                alt={reward.title}
                                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                                                                <Gift className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                                                            </div>
                                                        )}

                                                        {/* Price Badge Overlay */}
                                                        <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg border border-white/20">
                                                            <span className="text-sm font-black text-purple-600 dark:text-purple-400">
                                                                {reward.cost_points.toLocaleString('pt-BR')} <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">pts</span>
                                                            </span>
                                                        </div>

                                                        {/* Out of Stock Overlay */}
                                                        {isOutOfStock && (
                                                            <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-[2px] flex items-center justify-center">
                                                                <span className="px-4 py-2 bg-gray-900 text-white dark:bg-white dark:text-gray-900 text-sm font-bold rounded-full shadow-xl">
                                                                    Esgotado
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Content Area */}
                                                    <div className="p-5 flex flex-col flex-1">
                                                        <div className="flex-1">
                                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                                                {reward.title}
                                                            </h3>
                                                            {(() => {
                                                                const isProduct = reward.reward_type === 'product';
                                                                const baseDesc = reward.description || '';
                                                                const descriptionText = isProduct
                                                                    ? `${baseDesc}\n\n🚚 Entrega de Produtos Físicos\nOs produtos físicos estão disponíveis apenas para regiões com cobertura de frete grátis dentro do Brasil.\n\nCaso o CEP informado não seja elegível para frete grátis, o pedido não poderá ser concluído e será automaticamente cancelado.`
                                                                    : baseDesc;

                                                                if (!descriptionText) return null;

                                                                const isExpanded = expandedIds.includes(reward.id);
                                                                const isLong = descriptionText.length > 120 || descriptionText.split('\n').length > 3;

                                                                return (
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                                                                        <div className={!isExpanded ? "line-clamp-3" : ""}>
                                                                            {descriptionText}
                                                                        </div>
                                                                        {isLong && (
                                                                            <button
                                                                                onClick={() => toggleExpand(reward.id)}
                                                                                className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold text-xs mt-1 transition-colors"
                                                                            >
                                                                                {isExpanded ? "Ler menos" : "Ler mais..."}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Stock Info */}
                                                        {reward.stock !== null && !isOutOfStock && (
                                                            <p className="text-xs font-semibold text-orange-500 mt-3 flex items-center gap-1.5">
                                                                <AlertCircle className="w-3.5 h-3.5" />
                                                                Restam apenas {reward.stock} un.
                                                            </p>
                                                        )}

                                                        <div className="mt-5">
                                                            <Button
                                                                variant={hasEnoughPoints && !isOutOfStock ? 'primary' : 'secondary'}
                                                                className={`w-full ${!hasEnoughPoints || isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 border-0 shadow-lg shadow-purple-500/25'}`}
                                                                onClick={() => handleSelectReward(reward)}
                                                                disabled={!hasEnoughPoints || isOutOfStock || isRedeeming}
                                                                isLoading={isRedeeming}
                                                            >
                                                                <ShoppingCart className="w-4 h-4 mr-2" />
                                                                {!hasEnoughPoints
                                                                    ? 'Pontos insuficientes'
                                                                    : isOutOfStock
                                                                        ? 'Indisponível'
                                                                        : 'Adquirir Recompensa'}
                                                            </Button>
                                                        </div>
                                                    </div>

                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Pagination Controls */}
                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-center gap-4 mt-8">
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-white"
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                                Página {currentPage} de {totalPages}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-white"
                                            >
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
